
import { BlogPost } from '@/types/blog';

export const post: BlogPost = {
  slug: "deep-dive-site-assessment",
  title: "Deep Dive: How Clean Tech Uses Fordje for Site Assessment",
  excerpt: "Once a promising location has been identified, the next big question is: Can we actually build this here? Learn how Fordje helps teams validate site feasibility early in the sales process.",
  content: `
    <p>
      Once a <a href="/blog/deep-dive-how-teams-use-fordje-for-prospecting" class="text-fordje-blue hover:text-fordje-accent underline">promising location has been identified</a>, the next big question is: <em>Can we actually build this here?</em>
    </p>
    
    <p>
      For clean tech teams, this is where a specific parcel, house, or commercial building gets evaluated to determine if the proposed system is viable - both technically and legally.
    </p>
    
    <p>
      It's not just about production potential or customer demand. It's about confirming whether the system that's been sold - batteries in the garage, ground mounts, a specific system size - can actually be permitted under local code.
    </p>
    
    <h2>What Site Assessment Looks Like Today</h2>
    
    <p>
      Site assessment often starts with a high-quality lead and a high-potential site. The system is modeled, the customer is on board, and everything looks great - until local rules get in the way.
    </p>
    
    <p>Teams today are left scrambling to confirm:</p>
    
    <ul>
      <li>Is a garage battery install allowed in this AHJ?</li>
      <li>Do fire setbacks reduce the maximum system size?</li>
      <li>Are there local design rules - like height limits or facade restrictions - that affect our proposed layout?</li>
    </ul>
    
    <p>
      And more often than not, the answers are hard to find - buried in PDFs, scattered across outdated AHJ websites, or pieced together in a shared Excel spreadsheet that hasn't been updated in months.
    </p>
    
    <p>
      This is where the sales process starts to break down. When unknowns pop up late in the game, projects get delayed or scaled back, and trust erodes.
    </p>
    
    <p>This is the phase where teams want <em>no surprises.</em></p>
    
    <h2>How Fordje Helps</h2>
    
    <p>
      Fordje gives solar sales and development teams a way to validate what's possible on a specific site - early in the sales process.
    </p>
    
    <p>Using the <strong>Synthesize Tool</strong>, teams can:</p>
    
    <ul>
      <li>Select key AHJ data points like fire setbacks, system sizing caps, battery location requirements, and zoning overlays</li>
      <li>Plug them directly into solar sales tools like <strong><a href="https://aurorasolar.com/sales-mode/" class="text-fordje-blue hover:text-fordje-accent underline" target="_blank" rel="noopener noreferrer">Aurora Solar</a></strong>, or reference them alongside <strong><a href="https://raptormaps.com/" class="text-fordje-blue hover:text-fordje-accent underline" target="_blank" rel="noopener noreferrer">Raptor Maps</a></strong> imagery and parcel data</li>
      <li>Quickly determine if what's been sold can actually be built. And if not, adjust the proposal or open a conversation with the customer</li>
    </ul>
    
    <p>
      This early check helps teams avoid over-promising and sets clearer expectations from day one. Instead of discovering constraints during plan review or permit submission, Fordje surfaces them up front.
    </p>
    
    <h2>Who Uses It</h2>
    
    <ul>
      <li>Residential and C&I solar sales teams</li>
      <li>Business Development</li>
    </ul>
    
    <h2>The Outcome</h2>
    
    <p>
      Site assessment is where deals are won or lost. The fastest way to lose momentum is to hit an AHJ surprise late in the process.
    </p>
    
    <p>Fordje helps teams:</p>
    
    <ul>
      <li>Validate feasibility at the same time a deal is being structured</li>
      <li>Align system proposals with real local requirements</li>
      <li>Protect timelines, budgets, and customer trust</li>
    </ul>
    
    <p>
      In our next post, we'll explore how teams carry that context forward into <strong>Design and Engineering</strong>, making sure what gets drawn is ready to submit.
    </p>
  `,
  coverImage: "/lovable-uploads/9257df8b-f828-410b-9d1a-0a8a32fd0367.png",
  date: "2025-06-16T12:00:00",
  readTime: 4,
  categories: ["Product", "Clean Tech", "Site Assessment"],
  author: {
    name: "Gillian (Wildfire) Sowray",
    title: "Co-founder & CEO",
    avatar: "/lovable-uploads/69c2731c-f0dc-41d3-8812-8b392d7302c2.png"
  }
};
